/*
 * MARKO ATHLETICS — kinetic navigation.
 *
 * Port of a React/GSAP component to a plain Liquid section. No framework:
 * the storefront is Horizon/Liquid, and shipping React + ReactDOM to hydrate
 * one overlay would cost ~180KB gzipped against a first-paint-sensitive PDP.
 * GSAP core + CustomEase is ~30KB and does the whole job.
 *
 * Degradation ladder, in order:
 *   1. No JS at all      -> snippets/marko-nav-css.liquid opens the overlay
 *                           on .is-open via a CSS clip-path transition.
 *   2. JS but no GSAP    -> same CSS path; we never strip the transition.
 *   3. Reduced motion    -> instant open, no tweens.
 *   4. Everything loaded -> the timeline below.
 *
 * Because of (1), the resting states (links pushed below their mask, rules
 * collapsed, meta hidden) are set HERE and never in the stylesheet. If they
 * lived in CSS and this file 404'd, the overlay would open onto a black void.
 */
(function () {
  'use strict';

  var EASE = 'mkNavEase';
  var easeReady = false;

  function makeEase() {
    if (easeReady || !window.gsap || !window.CustomEase) return easeReady;
    // Same curve as the source component's cubic-bezier(0.65, 0.05, 0, 1):
    // a hard launch that decelerates for most of its life. Registered once
    // globally rather than per-instance so section reloads don't stack it.
    window.gsap.registerPlugin(window.CustomEase);
    if (!window.CustomEase.get(EASE)) {
      window.CustomEase.create(EASE, '0.65, 0.05, 0, 1');
    }
    easeReady = true;
    return true;
  }

  function scrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  // Where the button lives when the overlay is closed: the theme header's
  // left column, immediately before the logo. Docking it into the real
  // header is what keeps it on the logo's row at every scroll position --
  // a fixed button would have to chase the announcement bar's height as the
  // sticky header slides under it.
  var DOCK = '.header__column--left';

  function dock(trigger) {
    var host = document.querySelector(DOCK);
    if (!host) return null;
    if (trigger.parentElement !== host) host.insertBefore(trigger, host.firstChild);
    trigger.classList.add('mk-navtrig--inline');
    return host;
  }

  function init(root) {
    if (!root || root.__mkNavBound) return;
    var trigger = root.querySelector('.mk-navtrig');
    var nav = root.querySelector('.mk-nav');
    if (!trigger || !nav) return;
    root.__mkNavBound = true;

    var rules = nav.querySelectorAll('.mk-nav__rules i');
    var links = nav.querySelectorAll('.mk-nav__link');
    var meta = nav.querySelector('.mk-nav__meta');
    var focusables = nav.querySelectorAll('a[href], button:not([disabled])');

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    var open = false;
    var tl = null;
    var host = dock(trigger);
    root.__mkTrigger = trigger;

    // Stand-in that holds the button's exact width in the header's flex row
    // while the button itself is floating. Without it the row collapses by
    // the button's width plus the column gap, the logo slides left behind
    // the overlay, and snaps back into place as the wipe uncovers it.
    var ghost = null;

    // Lift the button out of the header and pin it where it already sits, so
    // it renders above the overlay without appearing to jump.
    function float() {
      if (!host || ghost) return;
      var r = trigger.getBoundingClientRect();

      ghost = document.createElement('span');
      ghost.className = 'mk-navtrig__ghost';
      ghost.setAttribute('aria-hidden', 'true');
      ghost.style.width = r.width + 'px';
      ghost.style.height = r.height + 'px';
      host.replaceChild(ghost, trigger);

      trigger.style.top = r.top + 'px';
      trigger.style.left = r.left + 'px';
      trigger.style.width = r.width + 'px';
      trigger.style.height = r.height + 'px';
      trigger.style.margin = '0';
      trigger.classList.add('mk-navtrig--floating');
      document.body.appendChild(trigger);
    }

    function unfloat() {
      if (!host || !ghost) return;
      trigger.classList.remove('mk-navtrig--floating');
      trigger.removeAttribute('style');
      // Swap straight back into the slot the ghost was holding — no reflow,
      // so the logo never moves in either direction. If the header re-rendered
      // while the overlay was open the ghost went with it, so fall back to a
      // plain re-dock; throwing here would skip the re-arm calls that follow.
      if (ghost.parentNode) ghost.parentNode.replaceChild(trigger, ghost);
      else dock(trigger);
      ghost = null;
    }

    var gsapReady = !!window.gsap && makeEase();

    // Claim clip-path from CSS only once we can actually drive it, so the
    // two systems never tween the same property against each other.
    if (gsapReady && !reduced.matches) {
      nav.classList.add('is-scripted');
      window.gsap.set(nav, { clipPath: 'inset(0% 0 100% 0)' });
      window.gsap.set(rules, { scaleY: 0 });
      window.gsap.set(links, { yPercent: 110 });
      window.gsap.set(meta, { opacity: 0 });
    }

    function buildOpen() {
      var t = window.gsap.timeline({
        defaults: { ease: EASE },
        onStart: function () {
          nav.style.visibility = 'visible';
          nav.style.pointerEvents = 'auto';
        }
      });
      t.to(nav, { clipPath: 'inset(0% 0 0% 0)', duration: 0.85 }, 0)
        // Rules erect themselves fractionally ahead of the type, so the
        // structure reads as arriving first and the words landing into it.
        .to(rules, { scaleY: 1, duration: 0.7, stagger: 0.055 }, 0.12)
        .to(links, { yPercent: 0, duration: 0.72, stagger: 0.07 }, 0.26)
        .to(meta, { opacity: 1, duration: 0.5, ease: 'power2.out' }, 0.62);
      return t;
    }

    function buildClose() {
      var t = window.gsap.timeline({
        defaults: { ease: EASE },
        onComplete: function () {
          nav.style.visibility = 'hidden';
          nav.style.pointerEvents = 'none';
          // Only now — dropping it back into the header mid-wipe would hide
          // the button behind the overlay for the rest of the animation.
          unfloat();
          // Re-arm for the next open.
          window.gsap.set(rules, { scaleY: 0 });
          window.gsap.set(links, { yPercent: 110 });
          window.gsap.set(meta, { opacity: 0 });
        }
      });
      // Type leaves upward — the direction it came from — then the panel
      // retracts to the top edge. Exit is ~40% faster than entry; a close
      // that takes as long as an open feels like the site is arguing.
      t.to(links, { yPercent: -110, duration: 0.42, stagger: 0.035 }, 0)
        .to(meta, { opacity: 0, duration: 0.2 }, 0)
        .to(rules, { scaleY: 0, duration: 0.35, stagger: 0.03 }, 0.05)
        .to(nav, { clipPath: 'inset(0% 0 100% 0)', duration: 0.5 }, 0.22);
      return t;
    }

    function lock(on) {
      if (on) {
        document.body.style.setProperty('--mk-sbw', scrollbarWidth() + 'px');
        document.body.classList.add('mk-nav-open');
      } else {
        document.body.classList.remove('mk-nav-open');
        document.body.style.removeProperty('--mk-sbw');
      }
    }

    function setState(next) {
      if (next === open) return;
      open = next;
      trigger.setAttribute('aria-expanded', String(open));
      nav.setAttribute('aria-hidden', String(!open));
      lock(open);

      if (open) float();

      if (!gsapReady || reduced.matches) {
        nav.classList.toggle('is-open', open);
        // No timeline to hang the restore off, so mirror the CSS duration.
        if (!open) window.setTimeout(unfloat, reduced.matches ? 0 : 850);
      } else {
        if (tl) tl.kill();
        tl = open ? buildOpen() : buildClose();
      }

      if (open) {
        // Defer past the class/timeline flip: focusing a visibility:hidden
        // element is a no-op and we'd silently lose the keyboard user.
        window.requestAnimationFrame(function () {
          if (focusables.length) focusables[0].focus({ preventScroll: true });
        });
      } else {
        trigger.focus({ preventScroll: true });
      }
    }

    trigger.addEventListener('click', function () {
      setState(!open);
    });

    document.addEventListener('keydown', function (e) {
      if (!open) return;
      if (e.key === 'Escape') {
        setState(false);
        return;
      }
      // Contain Tab inside the overlay. Without this, tabbing walks into the
      // page behind a fullscreen panel, which for a screen-reader user looks
      // exactly like the site has stopped responding.
      if (e.key !== 'Tab' || !focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // The overlay is fixed and outlives a soft navigation in the theme
    // editor; close it so it can't strand the merchant on a black screen.
    document.addEventListener('shopify:section:select', function () {
      if (open) setState(false);
    });
  }

  function boot() {
    document.querySelectorAll('[data-mk-nav-root]').forEach(function (root) {
      // The button lives inside the header, so a header re-render in the
      // theme editor takes it down with it. Re-dock the same node — it still
      // carries its listeners — rather than rebuilding and double-binding.
      if (root.__mkNavBound) {
        if (root.__mkTrigger && !document.contains(root.__mkTrigger)) dock(root.__mkTrigger);
        return;
      }
      init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();